// src/pages/events/Questionnaires.jsx
import { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid, Chip, Stack, Tabs, Tab,
  Checkbox, FormControlLabel, Divider, MenuItem, IconButton, Autocomplete,
  Paper, Menu, ListItemIcon, ListItemText, Tooltip, InputAdornment, Avatar,
  Accordion, AccordionSummary, AccordionDetails, CircularProgress
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import PublishIcon from '@mui/icons-material/Publish';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import BarChartIcon from '@mui/icons-material/BarChart';
import SearchIcon from '@mui/icons-material/Search';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import GroupIcon from '@mui/icons-material/Group';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);
import {
  watchEvents, createQuestionnaire, createPeerReviewQuestionnaire,
  publishEvent, unpublishEvent, deleteEvent as apiDeleteEvent,
} from '../../api/events';
import { ref, onValue } from 'firebase/database';
import { db } from '../../firebase';
import EventViewDialog from './EventViewDialog';
import ResultsDialog from './ResultsDialog';
import AttendancePointsDialog from './AttendancePointsDialog'; // <-- NEW
/* ------------------------ helpers ------------------------ */
const uid = () => (crypto?.randomUUID?.() || Math.random().toString(36).slice(2));
const toMs = (d) => (typeof d === 'number' ? d : dayjs(d).valueOf());
const fmtRange = (s, e) =>
  s && e ? `${new Date(s).toLocaleString()} → ${new Date(e).toLocaleString()}` : '—';
const fmtEntry = (t) => (t ? `Entry: ${new Date(t).toLocaleString()}` : '');
const withinMonth = (ms, monthStartDayjs) => {
  if (!ms || !monthStartDayjs?.isValid?.()) return true; // no filter applied
  const start = monthStartDayjs.startOf('month').valueOf();
  const end = monthStartDayjs.endOf('month').valueOf();
  return ms >= start && ms <= end;
};
const relBadge = (e) => {
  const s = e?.dates?.start ?? 0;
  const en = e?.dates?.end ?? 0;
  const now = Date.now();
  if (en && now > en) return `Ended ${dayjs(en).fromNow()}`;
  if (s && now < s) return `Starts ${dayjs(s).fromNow()}`;
  if (s && en && now >= s && now <= en) return `Ends ${dayjs(en).fromNow()}`;
  return '';
};
// DateTimePicker defaults – show date+time in input
const DT_PROPS = {
  ampm: false, // 24h
  format: 'DD/MM/YYYY, HH:mm',
  views: ['year', 'month', 'day', 'hours', 'minutes'],
};
/* -------------------- UX atoms -------------------- */
const Pill = ({ icon, label, value, color = 'primary' }) => (
  <Stack
    direction="row"
    spacing={1}
    alignItems="center"
    sx={{
      px: 1.25, py: 0.75, borderRadius: 999,
      bgcolor: (t) => t.palette.action.hover,
      '& .val': { fontWeight: 800 }
    }}
  >
    <Avatar sx={{ width: 24, height: 24, bgcolor: `${color}.light`, color: `${color}.dark` }}>
      {icon}
    </Avatar>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" className="val">{value}</Typography>
  </Stack>
);
const EmptyState = ({ title, subtitle, icon }) => (
  <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
    <Box sx={{ display: 'inline-flex', p: 1.25, borderRadius: 2, bgcolor: 'action.hover', mb: 1 }}>
      {icon ?? <InfoOutlinedIcon />}
    </Box>
    <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
    {subtitle && <Typography variant="body2" sx={{ mt: 0.5 }}>{subtitle}</Typography>
    }
  </Box>
);
/* ---------- suggestions ---------- */
const SUGGEST_STANDARD = [
  { type: 'MCQ', text: 'This session improved my knowledge' },
  { type: 'MCQ', text: 'I would recommend this training' },
  { type: 'TEXT', text: 'One thing I learned' },
  { type: 'TEXT', text: 'Any suggestions for improvement?' },
];
const SUGGEST_PEER = [
  'Behaviour','Collaboration','Communication','Reliability','Initiative',
  'Problem solving','Learning attitude','Leadership','Feedback (optional)'
];
/* ---------- defaults ---------- */
// QUIZ: MCQ + TEXT only
const seedStandard = [
  { id: uid(), type: 'MCQ', label: 'Question 1', options: ['', '', '', ''], weight: 1, required: true, correctAnswer: null },
  { id: uid(), type: 'TEXT', label: 'Short answer', weight: 0, required: false },
];
// Peer review (unchanged)
const seedPeer = [
  { id: 'behaviour', type: 'STAR', label: 'Behaviour', weight: 0, required: true },
  { id: 'communication', type: 'STAR', label: 'Communication', weight: 0, required: true },
  { id: 'supportive', type: 'STAR', label: 'Supportive', weight: 0, required: true },
  { id: 'positive_attitude', type: 'STAR', label: 'Positive Attitude', weight: 0, required: true },
  { id: 'comment', type: 'TEXT', label: 'Feedback (optional)', weight: 0, required: false },
];
/* ---------- eligibility helpers (exact rule set) ---------- */
const hasProfile = (emp) => {
  const name =
    emp?.submittedProfile?.profileFields?.fullName ??
    emp?.fullName ?? '';
  return String(name || '').trim().length > 0;
};
const isActiveEligible = (emp) => {
  const status = String(emp?.onboardingStatus || '').toUpperCase();
  const active = emp?.active !== false;
  return active && status === 'COMPLETED' && hasProfile(emp);
};
/* ---------- helpers for negative marking note ---------- */
const NEG_NOTE = 'Note: Negative marking is enabled (–¼ for each wrong answer).';
function injectNegNote(text) {
  const t = String(text || '');
  return t.includes(NEG_NOTE) ? t : (t ? `${t}\n\n${NEG_NOTE}` : NEG_NOTE);
}
function stripNegNote(text) {
  const t = String(text || '');
  return t.replace(NEG_NOTE, '').replace(/\n{2,}$/,'').trim();
}
/* ---------- Quiz Question row (Accordion) ---------- */
function QuizQuestionRow({
  q, index, total,
  onChange, onDuplicate, onDelete, onMoveUp, onMoveDown,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const ensureOptionCount = (opts, nextLen) => {
    const base = Array.isArray(opts) ? [...opts] : [];
    const arr = base.slice(0, nextLen);
    while (arr.length < nextLen) arr.push('');
    return arr;
  };
  // Keep correctAnswer within bounds when options change
  const safeOnChange = (patch) => {
    if (patch?.options) {
      const len = patch.options.length;
      let nextCorrect = q.correctAnswer;
      if (!(Number.isInteger(nextCorrect) && nextCorrect >= 0 && nextCorrect < len)) {
        nextCorrect = null;
      }
      onChange({ ...patch, correctAnswer: nextCorrect });
    } else {
      onChange(patch);
    }
  };
  return (
    <Accordion disableGutters sx={{ borderRadius: 2, mb: 1.25, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display:'flex', alignItems:'center', gap: 1, width:'100%' }}>
          <Chip size="small" label={`Q${index + 1}`} />
          <Typography sx={{ fontWeight: 600, flex: 1, minWidth: 0 }} noWrap>
            {q.label || 'Untitled question'}
          </Typography>
          <Chip size="small" variant="outlined" label={q.type === 'MCQ' ? 'MCQ' : 'TEXT'} />
          {q.required && <Chip size="small" color="primary" label="Required" sx={{ ml: 1 }} />}
          <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}><MoreVertIcon fontSize="small" /></IconButton>
          <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
            <MenuItem disabled={index === 0} onClick={() => { setAnchorEl(null); onMoveUp(); }}>
              <ListItemIcon><ArrowUpwardIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Move up</ListItemText>
            </MenuItem>
            <MenuItem disabled={index === total - 1} onClick={() => { setAnchorEl(null); onMoveDown(); }}>
              <ListItemIcon><ArrowDownwardIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Move down</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { setAnchorEl(null); onDuplicate(); }}>
              <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Duplicate</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { setAnchorEl(null); onDelete(); }}>
              <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Question"
              value={q.label}
              onChange={e => safeOnChange({ label: e.target.value })}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              select fullWidth label="Type" value={q.type}
              onChange={e => safeOnChange({ type: e.target.value })}
            >
              <MenuItem value="MCQ">Multiple choice</MenuItem>
              <MenuItem value="TEXT">Text</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth type="number" label="Weight"
              value={q.weight ?? 0}
              onChange={e => safeOnChange({ weight: Number(e.target.value) || 0 })}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControlLabel
              sx={{ m: 0, width: '100%' }}
              control={<Checkbox checked={!!q.required} onChange={(e) => safeOnChange({ required: e.target.checked })} />}
              label="Req."
            />
          </Grid>
          <Grid item xs={12}>
            <Divider textAlign="left">Options</Divider>
          </Grid>
          {q.type === 'MCQ' && (
            <>
              <Grid item xs={12}>
                <TextField
                  select fullWidth label="Number of options"
                  value={Math.max(2, Math.min(6, (q.options || []).length || 4))}
                  onChange={(e) => {
                    const len = Math.max(2, Math.min(6, Number(e.target.value) || 4));
                    safeOnChange({ options: ensureOptionCount(q.options, len) });
                  }}
                >
                  {[2,3,4,5,6].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select fullWidth label="Correct answer"
                  value={Number.isInteger(q.correctAnswer) ? q.correctAnswer : ''}
                  onChange={(e) => {
                    const idx = e.target.value === '' ? null : Number(e.target.value);
                    safeOnChange({ correctAnswer: Number.isInteger(idx) ? idx : null });
                  }}
                >
                  <MenuItem value="">— Select —</MenuItem>
                  {(q.options || []).map((opt, idx) => (
                    <MenuItem key={idx} value={idx}>
                      {`Option ${idx + 1}${opt ? ` — ${opt}` : ''}`}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {(q.options || ensureOptionCount([], 4)).map((opt, k) => (
                <Grid item xs={12} sm={6} md={3} key={k}>
                  <TextField
                    fullWidth
                    label={`Option ${k + 1}`}
                    value={opt}
                    onChange={e => {
                      const next = [...(q.options || ensureOptionCount([], 4))];
                      next[k] = e.target.value;
                      safeOnChange({ options: next });
                    }}
                  />
                </Grid>
              ))}
            </>
          )}
          {q.type === 'TEXT' && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Free-text answer. (No options / No correct answer)
              </Typography>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}
/* ---- Prefill edit ---- */
function prefillForEdit(e, setTab, setForm, setPeerForm, setEditingId) {
  const isPeer = !!e?.peerReview?.enabled;
  setTab(isPeer ? 1 : 0);
  setEditingId(e.id);
  if (isPeer) {
    setPeerForm(p => ({
      ...p,
      title: e.title || '',
      description: e.description || '',
      instructions: e.instructions || '',
      status: e.status || 'DRAFT',
      dates: {
        ...p.dates,
        start: dayjs(e?.dates?.start || Date.now()),
        end: dayjs(e?.dates?.end || Date.now()),
        entryAllowedAt: dayjs(e?.dates?.entryAllowedAt || e?.dates?.start || Date.now()),
        durationMinutes: Number(e?.dates?.durationMinutes) || 30,
      },
      audience: e?.audience?.all
        ? { all: true, selected: [] }
        : { all: false, selected: (e?.audience?.selected || []).map(uid => ({ uid, fullName: uid })) },
      peerReview: {
        ...(p.peerReview || {}),
        enabled: true,
        includeSelf: !!e?.peerReview?.includeSelf,
        targets: e?.peerReview?.targets || [],
        schema: (e?.peerReview?.schema || []).map(q => ({ ...q, id: q.id || uid() })),
      }
    }));
  } else {
    setForm(f => ({
      ...f,
      title: e.title || '',
      description: e.description || '',
      instructions: e.instructions || '',
      status: e.status || 'DRAFT',
      dates: {
        ...f.dates,
        start: dayjs(e?.dates?.start || Date.now()),
        end: dayjs(e?.dates?.end || Date.now()),
        entryAllowedAt: dayjs(e?.dates?.entryAllowedAt || e?.dates?.start || Date.now()),
        durationMinutes: Number(e?.dates?.durationMinutes) || 30,
      },
      audience: e?.audience?.all
        ? { all: true, selected: [] }
        : { all: false, selected: (e?.audience?.selected || []).map(uid => ({ uid, fullName: uid })) },
      questionnaire: {
        negativeMarking: !!e?.questionnaire?.negativeMarking,
        questions: (e?.questionnaire?.questions || []).map(_q => {
          const type = _q.type === 'TEXT' ? 'TEXT' : 'MCQ';
          const base = {
            id: _q.id || uid(),
            type,
            label: String(_q.label || ''),
            weight: Number.isFinite(_q.weight) ? _q.weight : 0,
            required: !!_q.required,
          };
          if (type === 'MCQ') {
            const opts = Array.isArray(_q.options) ? _q.options : ['', '', '', ''];
            const correctAnswer =
              Number.isInteger(_q.correctAnswer) && _q.correctAnswer >= 0 && _q.correctAnswer < opts.length
                ? _q.correctAnswer
                : null;
            return { ...base, options: opts.map(s => String(s ?? '')), correctAnswer };
          }
          return base; // TEXT → no options key
        })
      }
    }));
  }
}
/* ============================= Component ============================= */
export default function Questionnaires() {
  const [events, setEvents] = useState({});
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [tab, setTab] = useState(0); // 0: standard (QUIZ), 1: peer
  const [employees, setEmployees] = useState({});
  const [listTab, setListTab] = useState(0); // 0 Draft, 1 Upcoming, 2 Ongoing, 3 Past
  const [confirm, setConfirm] = useState({ open: false, mode: null, id: null, title: '' });
  // warnings dialog (validation/errors)
  const [warnOpen, setWarnOpen] = useState(false);
  const [warnTitle, setWarnTitle] = useState('Please fix the following');
  const [warnIssues, setWarnIssues] = useState([]); // array of strings
  // saving/publishing spinners
  const [savingStandard, setSavingStandard] = useState(false);
  const [savingPeer, setSavingPeer] = useState(false);
  const [publishingId, setPublishingId] = useState(null);
  // search & calendar filter (month)
  const [query, setQuery] = useState('');
  const [monthFilter, setMonthFilter] = useState(null); // dayjs() | null
  // ----------- Standard QUIZ form -----------
  const [form, setForm] = useState({
    title: '',
    description: '',
    instructions: '',
    type: 'QUESTIONNAIRE',
    status: 'DRAFT',
    dates: {
      start: dayjs().minute(0).second(0).millisecond(0),
      end: dayjs().minute(0).second(0).millisecond(0).add(30, 'minute'),
      durationMinutes: 30,
      entryAllowedAt: dayjs().minute(0).second(0).millisecond(0),
    },
    audience: { all: true, selected: [] },
    questionnaire: { questions: seedStandard, negativeMarking: false },
  });
  // Bulk add dialog (QUIZ)
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  // ----------- Peer review form (unchanged behavior) -----------
  const [peerForm, setPeerForm] = useState({
    title: 'Thinker of the Month -',
    description: 'Rate your colleagues across behaviour, Communication, Supportive,Positive Attitude.',
    instructions: '🏆 Performer of the Month – \n\nInstructions & Criteria We are excited to announce the Performer of the Month recognition program — an initiative to appreciate and reward employees who consistently go above and beyond in their roles',
    type: 'QUESTIONNAIRE',
    status: 'DRAFT',
    dates: {
      start: dayjs().minute(0).second(0).millisecond(0),
      end: dayjs().minute(0).second(0).millisecond(0).add(30, 'minute'),
      durationMinutes: 30,
      entryAllowedAt: dayjs().minute(0).second(0).millisecond(0),
    },
    audience: { all: true, selected: [] },
    peerReview: {
      enabled: true,
      includeSelf: true,
      targets: [],
      schema: seedPeer,
    },
  });
  /* -------- subscriptions -------- */
  useEffect(() => watchEvents(setEvents), []);
  useEffect(() => {
    const unsub = onValue(ref(db, '/employees'), snap => setEmployees(snap.val() || {}));
    return () => unsub();
  }, []);
  const allEvents = useMemo(
    () => Object.values(events || {}).filter(e => e?.type === 'QUESTIONNAIRE'),
    [events]
  );
  /* -------- eligible employees -------- */
  const eligibleEmployeesArr = useMemo(() => {
    return Object.entries(employees || {})
      .filter(([, e]) => isActiveEligible(e))
      .map(([uid, e]) => ({
        uid,
        fullName: e?.submittedProfile?.profileFields?.fullName || e?.fullName || uid
      }));
  }, [employees]);
  const audienceOptions = useMemo(() => ([
    { uid: '__ALL__', fullName: `Select all eligible (${eligibleEmployeesArr.length})` },
    ...eligibleEmployeesArr
  ]), [eligibleEmployeesArr]);
  /* -------- list filtering -------- */
  const now = Date.now();
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allEvents.filter(e => {
      const inSearch = q === '' || (e?.title || '').toLowerCase().includes(q);
      const inMonth = withinMonth(e?.dates?.start ?? e?.dates?.end, monthFilter);
      return inSearch && inMonth;
    });
  }, [allEvents, query, monthFilter]);
  const groups = useMemo(() => {
    const draft = [], upcoming = [], ongoing = [], past = [];
    filtered.forEach((e) => {
      const status = e.status || 'DRAFT';
      const start = e?.dates?.start ?? 0;
      const end = e?.dates?.end ?? 0;
      if (status !== 'PUBLISHED') { draft.push(e); return; }
      if (end && now > end) { past.push(e); return; }
      if (start && now < start) { upcoming.push(e); return; }
      ongoing.push(e);
    });
    draft.sort((a,b) => (b?.updatedAt || 0) - (a?.updatedAt || 0));
    upcoming.sort((a,b) => (a?.dates?.start || 0) - (b?.dates?.start || 0));
    ongoing.sort((a,b) => (a?.dates?.end || 0) - (b?.dates?.end || 0));
    past.sort((a,b) => (b?.dates?.end || 0) - (a?.dates?.end || 0));
    return { draft, upcoming, ongoing, past };
  }, [filtered, now]);
  /* -------- Past: group by month -------- */
  function groupByMonth(list) {
    const m = {};
    list.forEach(e => {
      const end = e?.dates?.end || e?.dates?.start || 0;
      const d = new Date(end);
      const key = d.toLocaleString('default', { month: 'long', year: 'numeric' });
      if (!m[key]) m[key] = [];
      m[key].push(e);
    });
    Object.keys(m).forEach(k => m[k].sort((a,b) => (b?.dates?.end || 0) - (a?.dates?.end || 0)));
    return Object.entries(m).sort((a,b) => {
      const aD = new Date(a[0]); const bD = new Date(b[0]); return bD - aD;
    });
  }
  /* ===================== VALIDATION ===================== */
  function showIssues(title, issues) {
    setWarnTitle(title);
    setWarnIssues(issues);
    setWarnOpen(true);
  }
  function validateQuizForm(f) {
    const issues = [];
    const title = (f?.title || '').trim();
    if (!title) issues.push('Title is required.');
    const start = toMs(f?.dates?.start);
    const end = toMs(f?.dates?.end);
    const entry = toMs(f?.dates?.entryAllowedAt);
    if (!Number.isFinite(start)) issues.push('Start date/time is required.');
    if (!Number.isFinite(end)) issues.push('End date/time is required.');
    if (Number.isFinite(start) && Number.isFinite(end) && end <= start) {
      issues.push('End must be after Start.');
    }
    if (Number.isFinite(entry) && Number.isFinite(start) && entry > start) {
      issues.push('“Allow entry from” cannot be after Start.');
    }
    const qs = f?.questionnaire?.questions || [];
    if (qs.length === 0) issues.push('Add at least one question.');
    qs.forEach((q, idx) => {
      const qn = idx + 1;
      const label = (q?.label || '').trim();
      if (!label) issues.push(`Q${qn}: Question text is required.`);
      const type = q?.type === 'TEXT' ? 'TEXT' : 'MCQ';
      if (type === 'MCQ') {
        const opts = Array.isArray(q?.options) ? q.options : [];
        if (opts.length < 2) issues.push(`Q${qn}: MCQ needs at least 2 options.`);
        const empty = opts
          .map((o, i) => ({i, v: (o ?? '').trim()}))
          .filter(x => !x.v)
          .map(x => x.i + 1);
        if (empty.length > 0) issues.push(`Q${qn}: Option(s) ${empty.join(', ')} cannot be blank.`);
        if (!(Number.isInteger(q?.correctAnswer) && q.correctAnswer >= 0 && q.correctAnswer < opts.length)) {
          issues.push(`Q${qn}: Select a correct answer.`);
        }
      }
    });
    if (!f?.audience?.all) {
      const sel = f?.audience?.selected || [];
      if (sel.length === 0) issues.push('Audience: select at least one employee or choose “All eligible”.');
    }
    return issues;
  }
  function validateExistingStandardEvent(e) {
    const f = {
      title: e?.title,
      dates: e?.dates,
      audience: e?.audience,
      questionnaire: e?.questionnaire,
    };
    return validateQuizForm(f);
  }
  /* -------- standard (QUIZ) handlers -------- */
  const addStdQuestion = (q) =>
    setForm(f => ({ ...f, questionnaire: { ...f.questionnaire, questions: [...f.questionnaire.questions, q] } }));
  const updateStdQuestion = (i, patch) =>
    setForm(f => {
      const qs = [...f.questionnaire.questions];
      const curr = qs[i] || {};
      const next = { ...curr, ...patch };
      // normalize type switch
      if (patch.type === 'TEXT') {
        delete next.options;
        delete next.correctAnswer;
      }
      if (patch.type === 'MCQ' && !next.options) next.options = ['', '', '', '']; // ensure options exist
      // ensure correctAnswer within bounds
      if (next.type === 'MCQ') {
        const len = (next.options || []).length;
        if (!(Number.isInteger(next.correctAnswer) && next.correctAnswer >= 0 && next.correctAnswer < len)) {
          next.correctAnswer = null;
        }
      }
      qs[i] = next;
      return { ...f, questionnaire: { ...f.questionnaire, questions: qs } };
    });
  const removeStdQuestion = (i) =>
    setForm(f => ({ ...f, questionnaire: { ...f.questionnaire, questions: f.questionnaire.questions.filter((_, idx) => idx !== i) } }));
  const duplicateStdQuestion = (i) =>
    setForm(f => {
      const q = f.questionnaire.questions[i];
      const copy = { ...q, id: uid(), label: (q.label || '') + ' (copy)' };
      const qs = [...f.questionnaire.questions];
      qs.splice(i + 1, 0, copy);
      return { ...f, questionnaire: { ...f.questionnaire, questions: qs } };
    });
  const moveStdQuestion = (i, dir) =>
    setForm(f => {
      const qs = [...f.questionnaire.questions];
      const j = i + dir;
      if (j < 0 || j >= qs.length) return f;
      const tmp = qs[i]; qs[i] = qs[j]; qs[j] = tmp;
      return { ...f, questionnaire: { ...f.questionnaire, questions: qs } };
    });
  // -------- peer handlers (unchanged) --------
  const addPeerQuestion = (q) =>
    setPeerForm(p => ({ ...p, peerReview: { ...p.peerReview, schema: [...(p.peerReview?.schema || []), q] } }));
  const updatePeerQuestion = (i, patch) =>
    setPeerForm(p => {
      const schema = [...(p.peerReview?.schema || [])];
      schema[i] = { ...schema[i], ...patch };
      return { ...p, peerReview: { ...p.peerReview, schema } };
    });
  const removePeerQuestion = (i) =>
    setPeerForm(p => ({ ...p, peerReview: { ...p.peerReview, schema: (p.peerReview?.schema || []).filter((_, idx) => idx !== i) } }));
  const duplicatePeerQuestion = (i) =>
    setPeerForm(p => {
      const q = (p.peerReview?.schema || [])[i];
      const copy = { ...q, id: uid(), label: q.label + ' (copy)' };
      const schema = [...(p.peerReview?.schema || [])];
      schema.splice(i + 1, 0, copy);
      return { ...p, peerReview: { ...p.peerReview, schema } };
    });
  /* -------- audience handlers -------- */
  const onStdAudienceChange = (_, values) => {
    const isAll = values.some(v => v.uid === '__ALL__');
    if (isAll) {
      setForm(f => ({ ...f, audience: { all: true, selected: [] }}));
    } else {
      const onlyEligible = values.filter(v => eligibleEmployeesArr.some(e => e.uid === v.uid));
      setForm(f => ({ ...f, audience: { all: false, selected: onlyEligible }}));
    }
  };
  const onPeerAudienceChange = (_, values) => {
    const isAll = values.some(v => v.uid === '__ALL__');
    if (isAll) {
      setPeerForm(p => ({ ...p, audience: { all: true, selected: [] }}));
    } else {
      const onlyEligible = values.filter(v => eligibleEmployeesArr.some(e => e.uid === v.uid));
      setPeerForm(p => ({ ...p, audience: { all: false, selected: onlyEligible }}));
    }
  };
  /* -------- build clean QUIZ payload (no undefined) -------- */
  const buildCleanQuiz = () => {
    const startMs = toMs(form.dates.start);
    const cleanQs = (form?.questionnaire?.questions || []).map((q) => {
      const base = {
        id: q.id || uid(),
        type: q.type === 'TEXT' ? 'TEXT' : 'MCQ',
        label: String(q.label || '').trim(),
        weight: Number.isFinite(q.weight) ? q.weight : 0,
        required: !!q.required,
      };
      if (base.type === 'MCQ') {
        const opts = Array.isArray(q.options) ? q.options : [];
        const limited = opts.slice(0, 6);
        while (limited.length < 2) limited.push(''); // at least 2 options
        const correct =
          Number.isInteger(q.correctAnswer) && q.correctAnswer >= 0 && q.correctAnswer < limited.length
            ? q.correctAnswer
            : null;
        return { ...base, options: limited.map(s => String(s ?? '')), correctAnswer: correct };
      }
      // TEXT → do not include options / correctAnswer
      return base;
    });
    const eligibleIds = new Set(eligibleEmployeesArr.map(e => e.uid));
    const selectedIds = form.audience.all
      ? Array.from(eligibleIds)
      : (form.audience.selected || [])
          .map(x => x.uid || x)
          .filter(uid => eligibleIds.has(uid));
    const payload = {
      ...form,
      subtype: 'STANDARD', // quiz
      type: 'QUESTIONNAIRE',
      dates: {
        start: startMs,
        end: toMs(form.dates.end),
        entryAllowedAt: toMs(form.dates.entryAllowedAt),
        durationMinutes: Number(form.dates.durationMinutes) || 30,
      },
      audience: form.audience.all
        ? { all: true, selected: [] }
        : { all: false, selected: selectedIds },
      questionnaire: {
        questions: cleanQs,
        negativeMarking: !!form?.questionnaire?.negativeMarking
      },
    };
    return payload;
  };
  /* -------- create handlers (robust + strip undefined) -------- */
  const handleCreateStandard = async (publishImmediately = false) => {
    const issues = validateQuizForm(form);
    if (issues.length) {
      showIssues('Please fix the following to proceed', issues);
      return;
    }
    setSavingStandard(true);
    try {
      const clean = buildCleanQuiz();
      clean.status = 'DRAFT';
      // strip any accidental undefined deeply
      const toSend = JSON.parse(JSON.stringify(clean));
      const newIdRes = await createQuestionnaire(toSend);
      // handle both string id or object with id/key
      const createdId =
        typeof newIdRes === 'string' ? newIdRes :
        newIdRes?.id || newIdRes?.key || newIdRes?.insertId || null;
      if (publishImmediately) {
        if (createdId) {
          await publishEvent(createdId);
        } else {
          showIssues('Saved as draft, but not published', [
            'The API did not return a new event ID, so we could not auto-publish.',
            'You can publish it from the list once it appears.'
          ]);
        }
      }
      setOpen(false);
      setEditingId(null);
    } catch (err) {
      console.error('Create standard failed:', err);
      const message = (err && err.message) ? err.message : 'Unknown error while creating the event.';
      showIssues('Couldn’t save', [message]);
    } finally {
      setSavingStandard(false);
    }
  };
  const handleCreatePeer = async () => {
    setSavingPeer(true);
    try {
      const startMs = toMs(peerForm.dates.start);
      if (startMs < Date.now() - 60_000) {
        showIssues('Please fix the following to proceed', ['Start time is in the past. Pick a future time.']);
        return;
      }
      const eligibleIds = new Set(eligibleEmployeesArr.map(e => e.uid));
      const selectedIds = peerForm.audience.all
        ? Array.from(eligibleIds)
        : (peerForm.audience.selected || [])
            .map(x => x.uid || x)
            .filter(uid => eligibleIds.has(uid));
      const schema = (peerForm?.peerReview?.schema || []).map(q => ({
        id: q.id || uid(),
        type: q.type === 'TEXT' ? 'TEXT' : 'STAR',
        label: String(q.label || '').trim(),
        weight: Number.isFinite(q.weight) ? q.weight : 0,
        required: !!q.required
      }));
      const clean = {
        ...peerForm,
        subtype: 'PEER_REVIEW',
        type: 'QUESTIONNAIRE',
        status: 'DRAFT',
        dates: {
          start: startMs,
          end: toMs(peerForm.dates.end),
          entryAllowedAt: toMs(peerForm.dates.entryAllowedAt),
          durationMinutes: Number(peerForm.dates.durationMinutes) || 30,
        },
        audience: peerForm.audience.all
          ? { all: true, selected: [] }
          : { all: false, selected: selectedIds },
        peerReview: {
          enabled: true,
          includeSelf: !!peerForm?.peerReview?.includeSelf,
          targets: peerForm.audience.all ? Array.from(eligibleIds) : selectedIds,
          schema
        }
      };
      const toSend = JSON.parse(JSON.stringify(clean));
      await createPeerReviewQuestionnaire(toSend);
      setOpen(false);
      setEditingId(null);
    } catch (err) {
      console.error('Create peer failed:', err);
      const message = (err && err.message) ? err.message : 'Unknown error while creating the peer review.';
      showIssues('Couldn’t save', [message]);
    } finally {
      setSavingPeer(false);
    }
  };
  /* -------- view/results dialogs -------- */
  const [viewId, setViewId] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const currentViewEvent = useMemo(() => {
    if (!viewId) return null;
    return Object.values(events || {}).find(e => e.id === viewId) || null;
  }, [events, viewId]);
  const [resultsId, setResultsId] = useState(null);
  const [resultsOpen, setResultsOpen] = useState(false);
  const currentResultsEvent = useMemo(() => {
    if (!resultsId) return null;
    return Object.values(events || {}).find(e => e.id === resultsId) || null;
  }, [events, resultsId]);
  // -------- attendance dialog (NEW) --------
  const [attendanceOpen, setAttendanceOpen] = useState(false);
  const [attendanceEvent, setAttendanceEvent] = useState(null);
  /* -------- section permissions -------- */
  const canEdit = (section) => section === 'draft' || section === 'upcoming';
  const canDelete = (section) => section === 'draft' || section === 'upcoming';
  const canPublishToggle = (section, status) =>
    (section === 'draft' || section === 'upcoming' || section === 'ongoing') &&
    (status === 'PUBLISHED' || status === 'DRAFT');
  const askConfirm = (mode, e) =>
    setConfirm({ open: true, mode, id: e.id, title: e.title || 'Event' });
  const tryPublishWithValidation = async (id) => {
    setPublishingId(id);
    try {
      const e = Object.values(events || {}).find(x => x.id === id);
      if (!e) throw new Error('Event not found');
      // validate standard quiz before publishing
      const isQuiz = !e?.peerReview?.enabled;
      if (isQuiz) {
        const issues = validateExistingStandardEvent(e);
        if (issues.length) {
          showIssues('Cannot publish – please fix the following', issues);
          return;
        }
      }
      await publishEvent(id);
    } catch (err) {
      console.error('Publish failed:', err);
      const message = (err && err.message) ? err.message : 'Unknown error while publishing.';
      showIssues('Couldn’t publish', [message]);
    } finally {
      setPublishingId(null);
    }
  };
  const doConfirm = async () => {
    const { mode, id } = confirm;
    if (!id) return setConfirm({ open:false, mode:null, id:null, title:'' });
    if (mode === 'publish') await tryPublishWithValidation(id);
    if (mode === 'unpublish') {
      try {
        setPublishingId(id);
        await unpublishEvent(id);
      } catch (err) {
        console.error('Unpublish failed:', err);
        showIssues('Couldn’t unpublish', [(err && err.message) ? err.message : 'Unknown error while unpublishing.']);
      } finally {
        setPublishingId(null);
      }
    }
    if (mode === 'delete') {
      try {
        await apiDeleteEvent(id);
      } catch (err) {
        console.error('Delete failed:', err);
        showIssues('Couldn’t delete', [(err && err.message) ? err.message : 'Unknown error while deleting.']);
      }
    }
    setConfirm({ open:false, mode:null, id:null, title:'' });
  };
  /* -------- row renderer -------- */
  const renderRow = (e, section) => {
    const start = e?.dates?.start ?? 0;
    const end = e?.dates?.end ?? 0;
    const entry = e?.dates?.entryAllowedAt ?? null;
    const statusColor =
      e.status === 'PUBLISHED' ? 'success' :
      e.status === 'DRAFT' ? 'default' : 'warning';
    const isPublishing = publishingId === e.id;
    return (
      <tr key={e.id}>
        <td style={{ padding:'12px 6px' }}>
          <Typography sx={{ fontWeight: 700 }}>{e.title || '—'}</Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} alignItems="center" useFlexGap>
            <Typography variant="caption" color="text.secondary">
              {fmtRange(start, end)}
            </Typography>
            <Chip size="small" variant="outlined" label={relBadge(e)} />
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {fmtEntry(entry)} {e?.dates?.durationMinutes ? `• ${e.dates.durationMinutes} min` : ''}
          </Typography>
        </td>
        <td style={{ padding:'10px 6px' }}>
          <Pill
            icon={<GroupIcon fontSize="small" />}
            label="Type"
            value={e?.peerReview?.enabled ? 'PEER' : 'QUIZ'}
            color={e?.peerReview?.enabled ? 'secondary' : 'primary'}
          />
        </td>
        <td style={{ padding:'10px 6px' }}>
          <Chip size="small" label={e.status || 'DRAFT'} color={statusColor} />
          {/* SHOW ONLY IF results.declared === true */}
          {section === 'past' && e?.results?.declared === true && (
            <Chip size="small" sx={{ ml: 1 }} label="Result declared" color="success" />
          )}
        </td>
        <td style={{ padding:'10px 6px' }}>
          {e?.audience?.all ? 'All (eligible only)' : `${e?.audience?.selected?.length || 0} selected`}
        </td>
        <td style={{ padding:'10px 6px', display:'flex', gap:8, alignItems:'center' }}>
          <Button size="small" startIcon={<VisibilityIcon />} onClick={() => { setViewId(e.id); setViewOpen(true); }}>
            View
          </Button>
          {canEdit(section) && (
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => {
                setViewOpen(false);
                setOpen(true);
                prefillForEdit(e, setTab, setForm, setPeerForm, setEditingId);
              }}
            >
              Edit
            </Button>
          )}
          {canPublishToggle(section, e.status) && (
            e.status !== 'PUBLISHED' ? (
              <Button
                size="small"
                startIcon={isPublishing ? <CircularProgress size={14}/> : <PublishIcon />}
                disabled={isPublishing}
                onClick={() => askConfirm('publish', e)}
              >
                {isPublishing ? 'Publishing…' : 'Publish'}
              </Button>
            ) : (
              <Button
                size="small"
                startIcon={isPublishing ? <CircularProgress size={14}/> : <UnpublishedIcon />}
                disabled={isPublishing}
                onClick={() => askConfirm('unpublish', e)}
              >
                {isPublishing ? 'Working…' : 'Unpublish'}
              </Button>
            )
          )}
          {canDelete(section) && (
            <Tooltip title="Delete permanently">
              <Button color="error" size="small" startIcon={<DeleteIcon />} onClick={() => askConfirm('delete', e)}>
                Delete
              </Button>
            </Tooltip>
          )}
          {(section === 'ongoing' || section === 'past') && (
            <Button size="small" startIcon={<BarChartIcon />} onClick={() => { setResultsId(e.id); setResultsOpen(true); }}>
              Results
            </Button>
          )}
          {/* NEW: Add Attendance appears only for Past events */}
          {section === 'past' && (
            <Button
              size="small"
              startIcon={<EventAvailableIcon />}
              onClick={() => { setAttendanceEvent(e); setAttendanceOpen(true); }}
            >
              Add Points
            </Button>
          )}
        </td>
      </tr>
    );
  };
  const sectionData =
    listTab === 0 ? groups.draft :
    listTab === 1 ? groups.upcoming :
    listTab === 2 ? groups.ongoing : groups.past;
  /* ============================ RENDER ============================ */
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ textAlign: 'left' }}>
        {/* Header toolbar */}
        <Box
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            mb: 2, gap: 2, flexWrap: 'wrap'
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Questionnaires</Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Pill icon={<EventAvailableIcon fontSize="small" />} label="Draft" value={groups.draft.length} />
            <Pill icon={<PendingActionsIcon fontSize="small" />} label="Upcoming" value={groups.upcoming.length} color="secondary" />
            <Pill icon={<DoneAllIcon fontSize="small" />} label="Ongoing" value={groups.ongoing.length} color="success" />
            <TextField
              size="small"
              placeholder="Search by title…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
              sx={{ minWidth: 220 }}
            />
            {/* Month filter (year+month only) */}
            <DateTimePicker
              {...DT_PROPS}
              label="Filter by month"
              value={monthFilter}
              onChange={(v) => setMonthFilter(v)}
              views={['year','month']}
              format="MMM YYYY"
              slotProps={{ textField: { size: 'small' } }}
            />
            <Button variant="text" onClick={() => { setQuery(''); setMonthFilter(null); }}>
              Clear
            </Button>
            <Button
              variant="contained"
              onClick={() => { setOpen(true); setTab(0); setEditingId(null); }}
              sx={{ borderRadius: 999 }}
              startIcon={<AddIcon />}
            >
              Create Questionnaire
            </Button>
          </Stack>
        </Box>
        {/* List Card */}
        <Card>
          <CardContent>
            <Tabs value={listTab} onChange={(_, v) => setListTab(v)} sx={{ mb: 2 }}>
              <Tab label={`Draft (${groups.draft.length})`} />
              <Tab label={`Upcoming (${groups.upcoming.length})`} />
              <Tab label={`Ongoing (${groups.ongoing.length})`} />
              <Tab label={`Past (${groups.past.length})`} />
            </Tabs>
            {listTab === 3 ? (
              groupByMonth(groups.past).length === 0 ? (
                <EmptyState title="No past events found." subtitle="Completed questionnaires will appear here grouped by month." />
              ) : (
                groupByMonth(groups.past).map(([month, evs]) => (
                  <Box key={month} sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                      {month}
                    </Typography>
                    <Box component="table" sx={{ width: '100%', borderCollapse:'separate', borderSpacing: 0 }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign:'left', padding:'10px 6px' }}>Event</th>
                          <th style={{ textAlign:'left', padding:'10px 6px' }}>Subtype</th>
                          <th style={{ textAlign:'left', padding:'10px 6px' }}>Status</th>
                          <th style={{ textAlign:'left', padding:'10px 6px' }}>Audience</th>
                          <th style={{ textAlign:'left', padding:'10px 6px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {evs.map(e => renderRow(e, 'past'))}
                      </tbody>
                    </Box>
                  </Box>
                ))
              )
            ) : (
              (sectionData || []).length === 0 ? (
                <EmptyState
                  title="No events in this section."
                  subtitle={listTab === 0 ? 'Draft questionnaires you create will show up here.'
                    : listTab === 1 ? 'Any scheduled questionnaires appear here before they start.'
                    : 'Live questionnaires appear here until they end.'}
                />
              ) : (
                <Box component="table" sx={{ width: '100%', borderCollapse:'separate', borderSpacing: 0 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign:'left', padding:'10px 6px' }}>Event</th>
                      <th style={{ textAlign:'left', padding:'10px 6px' }}>Subtype</th>
                      <th style={{ textAlign:'left', padding:'10px 6px' }}>Status</th>
                      <th style={{ textAlign:'left', padding:'10px 6px' }}>Audience</th>
                      <th style={{ textAlign:'left', padding:'10px 6px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sectionData.map(e =>
                      renderRow(
                        e,
                        listTab === 0 ? 'draft' :
                        listTab === 1 ? 'upcoming' :
                        listTab === 2 ? 'ongoing' : 'past'
                      )
                    )}
                  </tbody>
                </Box>
              )
            )}
          </CardContent>
        </Card>
        {/* View dialog */}
        <EventViewDialog
          open={viewOpen}
          event={currentViewEvent}
          onClose={() => setViewOpen(false)}
          onEdit={() => {
            const ev = currentViewEvent;
            setViewOpen(false);
            setOpen(true);
            prefillForEdit(ev, setTab, setForm, setPeerForm, setEditingId);
          }}
        />
        {/* Results dialog */}
        <ResultsDialog
          open={resultsOpen}
          onClose={() => setResultsOpen(false)}
          event={currentResultsEvent}
        />
        {/* Attendance dialog (NEW) */}
        <AttendancePointsDialog
          open={attendanceOpen}
          onClose={() => { setAttendanceOpen(false); setAttendanceEvent(null); }}
          event={attendanceEvent}
        />
        {/* Publish/Unpublish/Delete confirm */}
        <Dialog open={confirm.open} onClose={() => setConfirm({ open:false, mode:null, id:null, title:'' })}>
          <DialogTitle>
            {confirm.mode === 'publish' && 'Publish event?'}
            {confirm.mode === 'unpublish' && 'Unpublish event?'}
            {confirm.mode === 'delete' && 'Delete event?'}
          </DialogTitle>
          <DialogContent dividers>
            <Typography>
              {confirm.mode === 'publish' && `“${confirm.title}” will become visible to targeted employees.`}
              {confirm.mode === 'unpublish' && `“${confirm.title}” will be hidden from employees.`}
              {confirm.mode === 'delete' && `“${confirm.title}” will be removed permanently. This cannot be undone.`}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              variant="text"
              onClick={() => setConfirm({ open:false, mode:null, id:null, title:'' })}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color={
                confirm.mode === 'delete' ? 'error'
                : confirm.mode === 'unpublish' ? 'warning'
                : 'primary'
              }
              startIcon={(publishingId && confirm.id === publishingId) ? <CircularProgress size={16}/> : null}
              disabled={publishingId && confirm.id === publishingId}
              onClick={doConfirm}
            >
              {confirm.mode === 'publish' && 'Publish'}
              {confirm.mode === 'unpublish' && 'Unpublish'}
              {confirm.mode === 'delete' && 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
        {/* Create / Edit dialog – Standard QUIZ + Peer Review */}
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle sx={{
            pb: 1.25,
            fontWeight: 800,
            background: 'linear-gradient(135deg,#5533ff22,#0ea5e922)',
          }}>
            {editingId ? 'Edit Questionnaire' : 'Create Questionnaire'}
          </DialogTitle>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 3, pt: 1 }}>
            <Tab label="Standard (Quiz)" />
            <Tab label="Performer of the Month (Peer Review)" />
          </Tabs>
          <DialogContent dividers sx={{ textAlign: 'left', pt: 2.5 }}>
            {/* ------------------- STANDARD QUIZ ------------------- */}
            {tab === 0 && (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth label="Title" placeholder="Quarterly quiz"
                      value={form.title}
                      onChange={e => setForm({ ...form, title: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth type="number" label="Duration (min)"
                      value={form.dates.durationMinutes}
                      onChange={e => setForm(f => ({
                        ...f, dates: { ...f.dates, durationMinutes: Number(e.target.value) || 30 }
                      }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth label="Short description"
                      value={form.description}
                      onChange={e => setForm({ ...form, description: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DateTimePicker
                      label="Start"
                      {...DT_PROPS}
                      value={form.dates.start}
                      onChange={(v) => {
                        const end = dayjs(v).add(form.dates.durationMinutes || 30, 'minute');
                        setForm(f => ({ ...f, dates: { ...f.dates, start: v, end, entryAllowedAt: v } }));
                      }}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DateTimePicker
                      label="End"
                      {...DT_PROPS}
                      value={form.dates.end}
                      onChange={(v) => setForm(f => ({ ...f, dates: { ...f.dates, end: v } }))}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DateTimePicker
                      label="Allow entry from"
                      {...DT_PROPS}
                      value={form.dates.entryAllowedAt}
                      onChange={(v) => setForm(f => ({ ...f, dates: { ...f.dates, entryAllowedAt: v } }))}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                  {/* Negative Marking Toggle */}
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!form?.questionnaire?.negativeMarking}
                          onChange={(e) => {
                            const enabled = e.target.checked;
                            setForm(f => ({
                              ...f,
                              instructions: enabled ? injectNegNote(f.instructions) : stripNegNote(f.instructions),
                              questionnaire: { ...f.questionnaire, negativeMarking: enabled }
                            }));
                          }}
                        />
                      }
                      label="Enable negative marking (–¼ for wrong answer)"
                      sx={{ m: 0 }}
                    />
                    {form?.questionnaire?.negativeMarking && (
                      <Typography variant="caption" color="text.secondary" sx={{ display:'block', mt: 0.5 }}>
                        The instructions will include: “{NEG_NOTE}”
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth multiline minRows={5}
                      label="Instructions (shown before Start)"
                      placeholder="Explain rules, scoring, and any constraints…"
                      value={form.instructions}
                      onChange={e => setForm({ ...form, instructions: e.target.value })}
                      sx={{ '& .MuiInputBase-root': { alignItems: 'flex-start' }, textarea: { fontSize: 14, lineHeight: 1.7 } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider textAlign="left" sx={{ mb: 1 }}>Audience</Divider>
                    <Autocomplete
                      multiple
                      fullWidth
                      options={audienceOptions}
                      getOptionLabel={(o) => o?.fullName ?? ''}
                      isOptionEqualToValue={(opt, val) => opt?.uid === val?.uid}
                      value={
                        form.audience.all
                          ? [{ uid: '__ALL__', fullName: `Select all eligible (${eligibleEmployeesArr.length})` }]
                          : (form.audience.selected || [])
                              .filter(x => x?.uid && eligibleEmployeesArr.some(e => e.uid === x.uid))
                      }
                      onChange={onStdAudienceChange}
                      renderInput={(params) => <TextField {...params} label="Select employees (eligible only)" placeholder="Type to search..." />}
                      renderOption={(props, option) => <li {...props} key={option.uid || String(option.fullName)}>{option.fullName}</li>}
                      sx={{ '& .MuiInputBase-root': { minHeight: 48 } }}
                    />
                  </Grid>
                </Grid>
                {/* Questions */}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <Divider textAlign="left">Questions</Divider>
                    <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap:'wrap', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => addStdQuestion({ id: uid(), type: 'MCQ', label: '', weight: 1, required: true, options: ['', '', '', ''], correctAnswer: null })}
                      >
                        Add MCQ
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => addStdQuestion({ id: uid(), type: 'TEXT', label: '', weight: 0, required: false })}
                      >
                        Add Text
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FileUploadIcon />}
                        onClick={() => setBulkOpen(true)}
                      >
                        Bulk add (1 per line)
                      </Button>
                      <Typography variant="caption" sx={{ ml: 'auto', alignSelf:'center', color: 'text.secondary' }}>
                        Total: {(form.questionnaire.questions || []).length} (works well up to ~30)
                      </Typography>
                    </Stack>
                    {(form.questionnaire.questions || []).map((q, i) => (
                      <QuizQuestionRow
                        key={q.id}
                        q={q}
                        index={i}
                        total={(form.questionnaire.questions || []).length}
                        onChange={(patch) => updateStdQuestion(i, patch)}
                        onDuplicate={() => duplicateStdQuestion(i)}
                        onDelete={() => removeStdQuestion(i)}
                        onMoveUp={() => moveStdQuestion(i, -1)}
                        onMoveDown={() => moveStdQuestion(i, +1)}
                      />
                    ))}
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>Suggested</Typography>
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                        {SUGGEST_STANDARD.map((s) => (
                          <Chip
                            key={s.text}
                            label={s.text}
                            onClick={() => addStdQuestion({
                              id: uid(),
                              type: s.type,
                              label: s.text,
                              weight: s.type === 'MCQ' ? 1 : 0,
                              required: s.type === 'MCQ',
                              options: s.type === 'MCQ' ? ['', '', '', ''] : undefined,
                              correctAnswer: s.type === 'MCQ' ? null : undefined
                            })}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </Grid>
                </Grid>
              </>
            )}
            {/* ------------------- PEER REVIEW (unchanged logic + helper note) ------------------- */}
            {tab === 1 && (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Title" value={peerForm.title} onChange={e => setPeerForm({ ...peerForm, title: e.target.value })} />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField fullWidth type="number" label="Duration (min)" value={peerForm.dates.durationMinutes}
                      onChange={e => setPeerForm(p => ({ ...p, dates: { ...p.dates, durationMinutes: Number(e.target.value) || 30 } }))} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Short description" value={peerForm.description} onChange={e => setPeerForm({ ...peerForm, description: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DateTimePicker
                      label="Start"
                      {...DT_PROPS}
                      value={peerForm.dates.start}
                      onChange={(v) => {
                        const end = dayjs(v).add(peerForm.dates.durationMinutes || 30, 'minute');
                        setPeerForm(p => ({ ...p, dates: { ...p.dates, start: v, end, entryAllowedAt: v } }));
                      }}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DateTimePicker
                      label="End"
                      {...DT_PROPS}
                      value={peerForm.dates.end}
                      onChange={(v) => setPeerForm(p => ({ ...p, dates: { ...p.dates, end: v } }))}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DateTimePicker
                      label="Allow entry from"
                      {...DT_PROPS}
                      value={peerForm.dates.entryAllowedAt}
                      onChange={(v) => setPeerForm(p => ({ ...p, dates: { ...p.dates, entryAllowedAt: v } }))}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth multiline minRows={6}
                      label="Instructions (shown before Start)"
                      placeholder="Explain how to rate colleagues, criteria, tips, etc."
                      value={peerForm.instructions}
                      onChange={e => setPeerForm({ ...peerForm, instructions: e.target.value })}
                      sx={{ '& .MuiInputBase-root': { alignItems: 'flex-start' }, textarea: { fontSize: 14, lineHeight: 1.7 } }}
                    />
                    {/* Non-breaking helper note about micro/VITO scoring */}
                    <Typography variant="caption" color="text.secondary" sx={{ display:'block', mt: 0.75 }}>
                      Tip: During peer rating, reviewers can also assign a tiny “micro (0–1)” VITO score (3-decimal) per employee.
                      It’s stored separately and shown in Results; no extra setup needed here.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!peerForm?.peerReview?.includeSelf}
                          onChange={e =>
                            setPeerForm(p => ({
                              ...p,
                              peerReview: { ...(p.peerReview || {}), includeSelf: e.target.checked }
                            }))
                          }
                        />
                      }
                      label="Include self-rating"
                      sx={{ m: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={9}>
                    <Divider textAlign="left" sx={{ mb: 1 }}>Audience</Divider>
                    <Autocomplete
                      multiple
                      fullWidth
                      options={audienceOptions}
                      getOptionLabel={(o) => o?.fullName ?? ''}
                      isOptionEqualToValue={(opt, val) => opt?.uid === val?.uid}
                      value={
                        peerForm.audience.all
                          ? [{ uid: '__ALL__', fullName: `Select all eligible (${eligibleEmployeesArr.length})` }]
                          : (peerForm.audience.selected || [])
                              .filter(x => x?.uid && eligibleEmployeesArr.some(e => e.uid === x.uid))
                      }
                      onChange={onPeerAudienceChange}
                      renderInput={(params) => (
                        <TextField {...params} label="Select employees (eligible only)" placeholder="Type to search..." />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.uid || String(option.fullName)}>{option.fullName}</li>
                      )}
                      sx={{ '& .MuiInputBase-root': { minHeight: 48 } }}
                    />
                  </Grid>
                </Grid>
                {/* Questions */}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <Divider textAlign="left">Questions</Divider>
                    {(peerForm.peerReview?.schema || []).map((q, i) => (
                      <Paper
                        key={q.id}
                        variant="outlined"
                        sx={{ px: 1.5, py: 1.25, borderRadius: 2, mb: 1.25, borderColor: 'divider' }}
                      >
                        <Grid container spacing={1} alignItems="center">
                          <Grid item xs={12} md={7}>
                            <TextField
                              fullWidth
                              label={`Q${i + 1} • question`}
                              value={q.label}
                              onChange={e => updatePeerQuestion(i, { label: e.target.value })}
                            />
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <TextField
                              select fullWidth label="Type" value={q.type}
                              onChange={e => updatePeerQuestion(i, { type: e.target.value === 'TEXT' ? 'TEXT' : 'STAR' })}
                            >
                              <MenuItem value="STAR">STAR rating (1–5)</MenuItem>
                              <MenuItem value="TEXT">Text</MenuItem>
                            </TextField>
                          </Grid>
                          <Grid item xs={4} md={1}>
                            <TextField
                              label="Weight" type="number" fullWidth
                              value={q.weight ?? 0}
                              onChange={e => updatePeerQuestion(i, { weight: Number(e.target.value) || 0 })}
                            />
                          </Grid>
                          <Grid item xs={2} md={1} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <FormControlLabel
                              sx={{ m: 0 }}
                              control={<Checkbox checked={!!q.required} onChange={e => updatePeerQuestion(i, { required: e.target.checked })} />}
                              label="Req."
                            />
                            <IconButton onClick={() => duplicatePeerQuestion(i)}><ContentCopyIcon /></IconButton>
                            <IconButton onClick={() => removePeerQuestion(i)}><DeleteIcon /></IconButton>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                    <Stack alignItems="center" sx={{ mt: 1 }}>
                      <Button variant="outlined" startIcon={<AddIcon />} onClick={() => addPeerQuestion({ id: uid(), type: 'STAR', label: '', weight: 0, required: true })}>
                        Add question
                      </Button>
                    </Stack>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>Suggested Questions</Typography>
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                        {SUGGEST_PEER.map((text) => (
                          <Chip
                            key={text}
                            label={text}
                            onClick={() => {
                              const isText = /feedback/i.test(text);
                              addPeerQuestion({
                                id: uid(),
                                type: isText ? 'TEXT' : 'STAR',
                                label: text,
                                weight: isText ? 0 : 25,
                                required: !isText
                              });
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </Grid>
                </Grid>
              </>
            )}
          </DialogContent>
          {/* sticky, safe footer */}
          <DialogActions sx={{ p: 2, position: 'sticky', bottom: 0, bgcolor: 'background.paper', gap: 1 }}>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            {tab === 0 ? (
              <>
                <Button
                  variant="outlined"
                  disabled={savingStandard}
                  endIcon={savingStandard ? <CircularProgress size={16}/> : null}
                  onClick={() => handleCreateStandard(false)}
                >
                  Save as Draft
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  disabled={savingStandard}
                  endIcon={savingStandard ? <CircularProgress size={16}/> : null}
                  onClick={() => handleCreateStandard(true)}
                >
                  Create & Publish
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                disabled={savingPeer}
                endIcon={savingPeer ? <CircularProgress size={16}/> : null}
                onClick={handleCreatePeer}
              >
                {editingId ? 'Save changes' : 'Create'}
              </Button>
            )}
          </DialogActions>
        </Dialog>
        {/* Bulk add dialog */}
        <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Bulk add questions</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
              Paste one question per line. We’ll create MCQs with 4 empty options. For free-text, add “ ::text” at the end.
            </Typography>
            <TextField
              multiline minRows={10} fullWidth
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder={`Example:\nWhat is our company name?\nWhen was TDO founded? ::text\nWhich department owns this process?`}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => {
                const lines = bulkText.split('\n').map(s => s.trim()).filter(Boolean);
                if (!lines.length) { setBulkOpen(false); return; }
                setForm(f => {
                  const add = lines.map(line => {
                    const isText = /::\s*text$/i.test(line);
                    const label = line.replace(/::\s*text$/i, '').trim();
                    return isText
                      ? { id: uid(), type: 'TEXT', label, weight: 0, required: false }
                      : { id: uid(), type: 'MCQ', label, weight: 1, required: true, options: ['', '', '', ''], correctAnswer: null };
                  });
                  return { ...f, questionnaire: { ...f.questionnaire, questions: [...f.questionnaire.questions, ...add] } };
                });
                setBulkOpen(false);
                setBulkText('');
              }}
            >
              Add
            </Button>
          </DialogActions>
        </Dialog>
        {/* Validation / error dialog */}
        <Dialog open={warnOpen} onClose={() => setWarnOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{warnTitle}</DialogTitle>
          <DialogContent dividers>
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              {warnIssues.map((txt, i) => (
                <li key={i}><Typography variant="body2">{txt}</Typography></li>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button variant="contained" onClick={() => setWarnOpen(false)}>OK</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
}